// src/pages/cources/page.tsx
import React from "react";
// Corrected imports for utils and strings from relative paths
import { Cources } from "../../constants/utils";
import strings from "../../constants/strings.json";
import { GraduationCapIcon, BookIcon, LaptopIcon, TeacherIcon } from "../../pages/components/Icons";

export default function WhyChooseSection({ locale = "en" }) {
    const localeStrings: any = strings;
    // Accesses the 'why_choose_us' object from strings.json
    const whyChoose = localeStrings[locale]?.why_choose_us ?? localeStrings["en"].why_choose_us ?? {};

    // Map icons to course categories
    const iconMap: Record<string, React.FC<any>> = {
        entrance: GraduationCapIcon,
        grade: BookIcon,
        board: TeacherIcon,
        supplementary: GraduationCapIcon,
    };

    // Vibrant unique gradients for each course
    const courseGradients = [
        { bg: "from-violet-500 to-purple-600", glow: "from-violet-400 to-purple-500" },
        { bg: "from-blue-500 to-cyan-600", glow: "from-blue-400 to-cyan-500" },
        { bg: "from-emerald-500 to-green-600", glow: "from-emerald-400 to-green-500" },
        { bg: "from-pink-500 to-rose-600", glow: "from-pink-400 to-rose-500" },
        { bg: "from-orange-500 to-amber-600", glow: "from-orange-400 to-amber-500" },
    ];

    return (
        <section
            id="services"
            className="relative bg-gradient-to-b from-gray-50 to-white py-14 md:py-16 px-4 overflow-hidden"
        >
            {/* Subtle animated background */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full blur-3xl opacity-30 animate-pulse" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full blur-3xl opacity-30 animate-pulse" style={{ animationDelay: "1s" }} />

            <div className="relative max-w-6xl mx-auto">
                {/* Header - Now using localized strings */}
                <div className="text-center mb-10">
          <span className="text-[10px] font-bold tracking-widest uppercase bg-gradient-to-r from-[#174143] to-[#3d9ea4] bg-clip-text text-transparent">
            {whyChoose.section_title || "Explore Our Courses"} {/* Section Title */}
          </span>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2">
                        {/* Ensure heading is constructed correctly */}
                        {/* The first word (Explore) is used as a fallback. */}
                        Explore Our <span className="bg-gradient-to-r from-[#174143] to-[#3d9ea4] bg-clip-text text-transparent">Courses</span>
                    </h2>
                    <p className="text-sm text-gray-600 max-w-xl mx-auto mt-2">
                        {whyChoose.description || "Comprehensive programs designed for your success"} {/* Description */}
                    </p>
                </div>

                {/* Courses Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Cources.map((course, index) => {
                        const colors = courseGradients[index % courseGradients.length];
                        const IconComponent = iconMap[course.category] || BookIcon;

                        return (
                            <div
                                key={index}
                                className="group relative bg-white rounded-2xl p-5 border border-gray-100 hover:border-transparent transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 overflow-hidden"
                                style={{
                                    animationDelay: `${index * 100}ms`,
                                    animation: "fadeInUp 0.6s ease-out forwards",
                                    opacity: 0,
                                }}
                            >
                                {/* Gradient overlay on hover */}
                                <div className={`absolute inset-0 bg-gradient-to-br ${colors.bg} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                                {/* Content */}
                                <div className="relative z-10">
                                    {/* Icon */}
                                    <div className="mb-4 flex justify-center">
                                        <div className={`w-14 h-14 bg-gradient-to-br ${colors.bg} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                                            <IconComponent className="w-7 h-7 text-white" />
                                        </div>
                                    </div>

                                    {/* Category tag */}
                                    {course.category && (
                                        <div className="mb-2 text-center">
                      <span className="inline-block px-2.5 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded-full bg-gray-100 text-gray-600 group-hover:bg-white/20 group-hover:text-white transition-all duration-300">
                        {course.category}
                      </span>
                                        </div>
                                    )}

                                    {/* Title */}
                                    <h3 className="text-base font-bold text-gray-900 text-center group-hover:text-white transition-colors duration-300">
                                        {course.title}
                                    </h3>

                                    {/* Description */}
                                    <p className="text-xs text-gray-600 text-center leading-relaxed line-clamp-2 mt-2 group-hover:text-white/90 transition-colors duration-300">
                                        {course.description}
                                    </p>

                                    {/* CTA Arrow */}
                                    <div className="flex items-center justify-center mt-3 text-[#174143] text-xs font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-0 group-hover:translate-x-1 group-hover:text-white">
                                        <span>Explore</span>
                                        <svg className="w-3.5 h-3.5 ml-1 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                        </svg>
                                    </div>
                                </div>

                                {/* Shine effect */}
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Bottom Badge */}
                <div className="mt-10 text-center">
                    <div className="inline-flex items-center gap-2.5 px-5 py-2.5 bg-white rounded-full shadow-lg border border-gray-100 text-xs font-medium text-gray-700 hover:shadow-xl hover:scale-105 transition-all duration-300">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
            </span>
                        Available in English & Kannada medium
                    </div>
                </div>
            </div>
        </section>
    );
}